import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreditCard, X, Clock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

/**
 * iter107 — Trial Activation Nudge.
 *
 * Recovers users who created an account but bailed out of the Stripe
 * Checkout step (i.e. signed up but never entered card details).
 *
 * Renders a small bottom-right "card" inviting them to complete their
 * 14-day Pro trial. Self-dismissible with two granularities:
 *   - "Maybe later" → hides for 24h (key: cc_trial_nudge_remind_until)
 *   - "Not interested" → hides for 30d (key: cc_trial_nudge_dismissed_until)
 *
 * Visibility rules (all must be true):
 *   • User is logged in
 *   • User is NOT pro
 *   • User does NOT have an active/trialing/past_due Stripe subscription
 *   • User does NOT have a free_pro_grant (lifetime users don't need this)
 *   • Account is older than 60 seconds (don't double-up with the welcome
 *     auto-redirect to /pro?welcome=1)
 *   • Route is NOT /pro, /login, /register, /auth/callback, /billing/*
 *   • LocalStorage dismissal hasn't expired
 */

const REMIND_KEY = "cc_trial_nudge_remind_until";
const DISMISS_KEY = "cc_trial_nudge_dismissed_until";
const REMIND_HOURS = 24;
const DISMISS_DAYS = 30;
const SUPPRESS_ROUTES = ["/pro", "/login", "/register", "/auth/callback", "/billing"];

function isSuppressedRoute(pathname) {
  return SUPPRESS_ROUTES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function readSuppression(key) {
  try {
    const v = localStorage.getItem(key);
    if (!v) return false;
    const ts = Number(v);
    if (!Number.isFinite(ts)) return false;
    return ts > Date.now();
  } catch {
    return false;
  }
}

export default function TrialActivationNudge() {
  // iter192 — paused. Product pivot: prioritise free-signup CTA sitewide
  // and remove the 14-day Pro trial nudge from every surface. The full
  // original component is preserved below (unreachable) so we can bring
  // it back verbatim when paid tiers relaunch.
  return null;
}

// eslint-disable-next-line no-unused-vars
function _PausedTrialActivationNudge() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [hidden, setHidden] = useState(false);

  // Fetch billing state to know if Stripe trial is already in flight
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/billing/me")
      .then(({ data }) => { if (alive) setBilling(data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  // Re-check suppression every render via state; also re-run when route changes
  useEffect(() => {
    setHidden(readSuppression(DISMISS_KEY) || readSuppression(REMIND_KEY));
  }, [location.pathname]);

  if (!user) return null;
  if (hidden) return null;
  if (isSuppressedRoute(location.pathname)) return null;
  if (!billing) return null;

  // Already paying / trialing — covered by TrialCountdownBar instead.
  if (["active", "trialing", "past_due"].includes(billing.stripe_status || "")) return null;
  // Lifetime / admin grant — never nudge.
  if (user.is_pro) return null;
  // Hide for very fresh signups (<60s) so the welcome auto-redirect handles them.
  try {
    const createdMs = new Date(user.created_at).getTime();
    if (Number.isFinite(createdMs) && Date.now() - createdMs < 60_000) return null;
  } catch { /* missing created_at — show nudge */ }

  const startTrial = () => {
    navigate("/pro?activate=1");
  };

  const remindLater = () => {
    try {
      localStorage.setItem(REMIND_KEY, String(Date.now() + REMIND_HOURS * 3600_000));
    } catch { /* private mode */ }
    setHidden(true);
  };

  const dismissLong = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400_000));
    } catch { /* private mode */ }
    setHidden(true);
  };

  return (
    <div
      className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6 max-w-[320px] w-[calc(100vw-2rem)] bg-white border-2 border-[#ff4500] shadow-[0_6px_0_0_#111] animate-fade-in"
      role="dialog"
      aria-label="Start your Pro trial"
      data-testid="trial-activation-nudge"
    >
      <div className="p-4">
        <button
          type="button"
          onClick={remindLater}
          aria-label="Dismiss for now"
          className="absolute top-2 right-2 text-[#888] hover:text-[#111] p-1"
          data-testid="trial-nudge-close"
        >
          <X className="w-4 h-4" strokeWidth={2.25} />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-4 h-4 text-[#ff4500]" strokeWidth={2.25} />
          <span className="eyebrow !text-[#ff4500]">14 days free Pro</span>
        </div>

        <div className="font-display text-base leading-tight mb-1.5">
          Finish setting up your free trial
        </div>
        <p className="text-[12px] text-[#555] leading-snug mb-3">
          Add a card to unlock <strong>Ask Camper</strong>, weather alerts,
          packing AI &amp; more for 14 days. <strong>£0 today</strong> —
          cancel anytime, charged £1.99/mo on day 15.
        </p>

        <button
          type="button"
          onClick={startTrial}
          className="w-full bg-[#ff4500] text-white text-[11px] font-bold uppercase tracking-wider py-2.5 shadow-[0_3px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#111] transition-transform"
          data-testid="trial-nudge-start"
        >
          Start my 14-day free trial →
        </button>

        <div className="mt-2 flex items-center justify-between text-[10px] text-[#777]">
          <button
            type="button"
            onClick={remindLater}
            className="inline-flex items-center gap-1 hover:text-[#111]"
            data-testid="trial-nudge-remind"
          >
            <Clock className="w-3 h-3" /> Maybe later
          </button>
          <button
            type="button"
            onClick={dismissLong}
            className="hover:text-[#111] underline underline-offset-2"
            data-testid="trial-nudge-dismiss-long"
          >
            Not interested
          </button>
        </div>
      </div>
    </div>
  );
}
